import {
  EActionCategory,
  ESdChangeReason,
  IAction,
  IActionDefinitionDto,
  IDisplayItem,
  IScreenDesign,
  RightPanelType,
  SpecialDIIds,
  ViewPart
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import * as React from "react";
import { ReactNode } from "react";
import ReactDOM from "react-dom";
import ActionService from "../../../../services/action-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import {
  formatQualifiedName,
  getActionDatasourceQualifier
} from "../../../../shared/utility/datasource-name-qualifier";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaComponentIcon from "../../../ui-kit/kuika-component-icon/kuika-component-icon";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./action-tree-panel.scss";

import SearchablePanelHeader from "../searchable-panel-header/searchable-panel-header";
export interface IDraggableActionPayload {
  actionDefinition: IActionDefinitionDto;
}

interface IIntermediateActionDefinition {
  actionDefinition: IActionDefinitionDto;
  diLocations?: {
    event: string;
    diRef: IDisplayItem;
    actionRef: IAction;
  }[];
}

export interface IActionTreePanelState {
  isSearchActive: boolean;
  searchText: string;
  componentMenuDropdownVisibility: { [key: string]: boolean };
  hoverItem?: string | null;
  expandedKeys: string[];
}

export interface IActionTreePanelProps {}

class ActionTreePanel extends PureSdComponent<IActionTreePanelProps & IWithSDProps, IActionTreePanelState> {
  constructor(props: IActionTreePanelProps & IWithSDProps) {
    super(props);
    this.state = {
      isSearchActive: false,
      searchText: "",
      componentMenuDropdownVisibility: {},
      hoverItem: null,
      expandedKeys: []
    };
  }

  componentDidMount() {
    this.handleSearchTextOnInit();
  }

  handleSearchTextOnInit = () => {
    const searchText = this.props.sd.searchTexts[ViewPart.ActionTreePanel];
    if (searchText && searchText.length > 0) {
      this.setState({ isSearchActive: true });
      this.handleSearchInputChange(searchText);
    }
  };

  deleteSearchText = (e): void => {
    e.stopPropagation();
    this.props.sd.setSearchTexts(ViewPart.ActionTreePanel, "");
    this.setState({ searchText: "", isSearchActive: false });
  };

  handleSearchInputChange = (value: string): void => {
    this.setState((prevState) => {
      const expandedKeys = value
        ? this.getAllPanelKeys(
            this.getUniqueActionDefinitionsFromDisplayItems(this.props.sd.getScreenDesign().displayItems)
          )
        : [];

      return {
        searchText: value,
        expandedKeys
      };
    });
    this.props.sd.setSearchTexts(ViewPart.ActionTreePanel, value);
  };

  private getAllPanelKeys = (actions: IIntermediateActionDefinition[]): string[] => {
    const keys: string[] = [];

    actions.forEach((action, i) => {
      keys.push(`action-tree__action-definition__${action.actionDefinition.id}${i}`);

      action.diLocations?.forEach((diLocation) => {
        if (diLocation.actionRef.ifThenElseData) {
          const { ifThenElseData } = diLocation.actionRef;

          if (ifThenElseData.ifActions?.length) {
            keys.push(`if-actions-${diLocation.actionRef.id}`);

            const nestedIfKeys = this.getAllPanelKeys(
              this.getUniqueActionDefinitionsFromIfThenElseData(ifThenElseData.ifActions, diLocation)
            );
            keys.push(...nestedIfKeys);
          }

          if (ifThenElseData.elseActions?.length) {
            keys.push(`else-actions-${diLocation.actionRef.id}`);

            const nestedElseKeys = this.getAllPanelKeys(
              this.getUniqueActionDefinitionsFromIfThenElseData(ifThenElseData.elseActions, diLocation)
            );
            keys.push(...nestedElseKeys);
          }
        }
      });
    });

    return keys;
  };

  private getUniqueActionDefinitionsFromDisplayItems = (
    displayItems: IDisplayItem[],
    intermediateActionDefinitions?: IIntermediateActionDefinition[]
  ): IIntermediateActionDefinition[] => {
    const _intermediateActionDefinitions: IIntermediateActionDefinition[] = intermediateActionDefinitions ?? [];

    displayItems.forEach((di) => {
      if (di.item && di.item.events) {
        di.item.events.forEach((event) => {
          event.actions.forEach((action) => {
            if (_intermediateActionDefinitions.some((iad) => iad.actionDefinition.id === action.actionDefinitionId)) {
              const _iad = _intermediateActionDefinitions.find(
                (iad) => iad.actionDefinition.id === action.actionDefinitionId
              );
              if (_iad) {
                _iad.diLocations?.push({
                  diRef: di,
                  event: event.eventName,
                  actionRef: action
                });
              }
            } else {
              const actionDefinition = this.props.sd.actionDefinitions.find(
                (ad) => ad.id === action.actionDefinitionId
              );
              if (actionDefinition) {
                _intermediateActionDefinitions.push({
                  actionDefinition,
                  diLocations: [
                    {
                      diRef: di,
                      event: event.eventName,
                      actionRef: action
                    }
                  ]
                });
              }
            }
          });
        });
      }

      if (di.children) {
        this.getUniqueActionDefinitionsFromDisplayItems(di.children, _intermediateActionDefinitions);
      }
    });

    return _intermediateActionDefinitions;
  };

  private getUniqueActionDefinitionsFromIfThenElseData = (actions: IAction[], diLocation: any) => {
    const _intermediateActionDefinitions: IIntermediateActionDefinition[] = [];
    actions.forEach((action) => {
      const actionDefinition = this.props.sd.actionDefinitions.find((ad) => ad.id === action.actionDefinitionId);
      if (actionDefinition) {
        if (!_intermediateActionDefinitions.some((iad) => iad.actionDefinition.id === action.actionDefinitionId)) {
          _intermediateActionDefinitions.push({
            actionDefinition,
            diLocations: [
              {
                diRef: diLocation.diRef,
                event: diLocation.event,
                actionRef: action
              }
            ]
          });
        }
      }
    });

    return _intermediateActionDefinitions;
  };

  private renderNode = (
    key: string,
    name: string,
    icon: ReactNode,
    children?: ReactNode,
    info?: string,
    onClick?: () => void,
    onDragStart?: (event: React.DragEvent<HTMLDivElement>) => void,
    infoBracketPair: "[]" | "()" = "[]"
  ) => {
    return (
      <>
        <KuikaMenuItem
          title={name}
          leftIcon={icon}
          onClick={onClick}
          draggable={!!onDragStart}
          onDragStart={onDragStart}
          onMouseEnter={() => {
            if (this.state.hoverItem !== key) {
              this.setState({ hoverItem: key });
            }
          }}
          onMouseLeave={() => {
            if (this.state.hoverItem === key) {
              this.setState({ hoverItem: null });
            }
          }}
          rightDropdown={
            <KuikaPopover
              title={name}
              visible={this.state.hoverItem === key}
              onVisibleChange={(visible) => {
                this.setState({ hoverItem: visible ? key : null });
              }}
              position="right"
              content={
                <div className="action-tree__info-popover__content">
                  {info && (
                    <div className="action-tree__info-popover__content--info">
                      <div className="kuika-flex-center--gap-xs">
                        <KuikaSmallText>Action triggered by</KuikaSmallText>
                        <KuikaSvg name="arrow-right" type="arrows" width={16} height={16} />
                      </div>
                      <KuikaSmallMediumText>
                        {infoBracketPair[0]}
                        {info}
                        {infoBracketPair[1]}
                      </KuikaSmallMediumText>
                    </div>
                  )}
                </div>
              }
              hideCloseIcon
            >
              <KuikaButton type="tertiary" size="sm" isIcon>
                <KuikaSvg name="arrow-right" type="arrows" width={16} height={16} />
              </KuikaButton>
            </KuikaPopover>
          }
        >
          {children}
        </KuikaMenuItem>
      </>
    );
  };

  private renderActions = (actions: IIntermediateActionDefinition[], isIfThenElseActions?: boolean) => {
    return (
      <KuikaCollapsePanel
        expandIcon={({ isActive }) =>
          isActive ? (
            <KuikaSvg name="k-down" type="arrows" width={16} height={16} />
          ) : (
            <KuikaSvg name="k-right" type="arrows" width={16} height={16} />
          )
        }
        activeKey={this.state.expandedKeys}
        onChange={(keys: any) => this.setState({ expandedKeys: keys })}
        items={actions
          .map((intermediateAction, i) => {
            const diLocations = intermediateAction.diLocations?.filter((diL) => {
              if (!this.state.searchText || this.state.searchText === "") return true;

              const searchText = this.state.searchText.toLowerCase();

              return (
                (diL.diRef.id === -1 && this.props.sd.getScreenDesign().name.toLowerCase().includes(searchText)) ||
                diL.diRef.item.name?.toLowerCase().includes(searchText) ||
                diL.diRef.title?.toLowerCase().includes(searchText) ||
                diL.diRef.friendlyName?.toLowerCase().includes(searchText) ||
                diL.event.toLowerCase().includes(searchText) ||
                diL.actionRef.label.toLowerCase().includes(searchText) ||
                diL.actionRef.tag?.toLowerCase().includes(searchText)
              );
            });

            if (!diLocations || diLocations.length === 0) return null;

            const isCustomAction =
              intermediateAction.actionDefinition?.actionCategory === EActionCategory.Custom ||
              intermediateAction.actionDefinition?.actionCategory === EActionCategory.Other;
            // One row per definition, so two datasources' same-named actions are two identical rows
            // unless the datasource is named in front (KOA-18147). Test and onboarding ids stay bare.
            const datasourceQualifier: string | undefined = getActionDatasourceQualifier(
              intermediateAction.actionDefinition,
              this.props.sd.actionDefinitions
            );

            const children = diLocations.map((diLocation) => {
              const isRootDi: boolean = diLocation.diRef.id === -1;
              let name: string;

              if (isRootDi) {
                name = this.props.sd.getScreenDesign().name;
              } else {
                name = diLocation.diRef.friendlyName ?? diLocation.diRef.title ?? diLocation.diRef.item.name ?? "";
              }

              const ifThenElseData = diLocation.actionRef.ifThenElseData;
              const ifActionsPanel = ifThenElseData?.ifActions?.length && ifThenElseData?.ifActions?.length > 0 && (
                <KuikaCollapsePanel
                  activeKey={this.state.expandedKeys}
                  onChange={(keys: any) => this.setState({ expandedKeys: keys })}
                  expandIcon={({ isActive }) =>
                    isActive ? (
                      <KuikaSvg name="k-down" type="arrows" width={16} height={16} />
                    ) : (
                      <KuikaSvg name="k-right" type="arrows" width={16} height={16} />
                    )
                  }
                  items={[
                    {
                      label: "If Actions",
                      key: `if-actions-${diLocation.actionRef.id}`,
                      children: this.renderActions(
                        this.getUniqueActionDefinitionsFromIfThenElseData(ifThenElseData.ifActions, diLocation),
                        true
                      )
                    }
                  ]}
                />
              );

              const elseActionsPanel = ifThenElseData?.elseActions?.length &&
                ifThenElseData?.elseActions?.length > 0 && (
                  <KuikaCollapsePanel
                    activeKey={this.state.expandedKeys}
                    onChange={(keys: any) => this.setState({ expandedKeys: keys })}
                    expandIcon={({ isActive }) =>
                      isActive ? (
                        <KuikaSvg name="k-down" type="arrows" width={16} height={16} />
                      ) : (
                        <KuikaSvg name="k-right" type="arrows" width={16} height={16} />
                      )
                    }
                    items={[
                      {
                        label: "Else Actions",
                        key: `else-actions-${diLocation.actionRef.id}`,
                        children: this.renderActions(
                          this.getUniqueActionDefinitionsFromIfThenElseData(ifThenElseData.elseActions, diLocation),
                          true
                        )
                      }
                    ]}
                  />
                );

              return (
                <>
                  {this.renderNode(
                    `action-tree__action__${diLocation.actionRef.id}${isIfThenElseActions ? "_if-then-else" : ""}${i}`,
                    name + (diLocation.actionRef.tag ? `#${diLocation.actionRef.tag}` : ""),
                    <KuikaComponentIcon
                      src={KMainFunctions.getImageUrlByComponentDefinition(
                        this.props.sd.getComponentDefinition(diLocation.diRef.item)
                      )}
                      width={20}
                      height={20}
                      className="action-tree__child-icon"
                      style={{ pointerEvents: "none" }}
                    />,
                    null,
                    diLocation.event,
                    () => {
                      this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
                      this.props.sd.setSelectedDisplayItem(diLocation.diRef.id.toString());
                    }
                  )}
                  {ifActionsPanel}
                  {elseActionsPanel}
                </>
              );
            });

            return {
              label: (
                <div className="kuika-flex--gap kuika-align-center">
                  {isCustomAction ? (
                    <svg
                      style={{ minWidth: 8, minHeight: 8 }}
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="8"
                      viewBox="0 0 8 8"
                      fill="none"
                    >
                      <circle cx="4" cy="4" r="3.5" fill="#9068FF" stroke="#C7B3FF" />
                    </svg>
                  ) : (
                    <svg
                      style={{ minWidth: 8, minHeight: 8 }}
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="8"
                      viewBox="0 0 8 8"
                      fill="none"
                    >
                      <circle cx="4" cy="4" r="3.5" fill="#FF8024" stroke="#FFB37B" />
                    </svg>
                  )}
                  <KuikaTextMedium
                    data-testid={intermediateAction.actionDefinition.name}
                    data-onboarding-id={`left-panel_action-tree_${intermediateAction.actionDefinition.name}`}
                    title={formatQualifiedName(intermediateAction.actionDefinition.name, datasourceQualifier)}
                  >
                    {formatQualifiedName(
                      KMainFunctions.getFormattedSystemActionName(intermediateAction.actionDefinition),
                      datasourceQualifier
                    )}
                  </KuikaTextMedium>
                </div>
              ),
              extra: ActionTreePanel.getIsActionDraggable(intermediateAction.actionDefinition) && (
                <KuikaButton
                  data-testid={`${intermediateAction.actionDefinition.name}DragButton`}
                  data-onboarding-id={`left-panel_action-tree_${intermediateAction.actionDefinition.name}-drag`}
                  type="tertiary"
                  size="sm"
                  isIcon
                  draggable
                  onDragStart={(event: React.DragEvent<HTMLDivElement>) =>
                    this.onDragStartAction(event, intermediateAction.actionDefinition)
                  }
                >
                  <KuikaSvg name="drag-drop" width={16} height={16} />
                </KuikaButton>
              ),
              key: `action-tree__action-definition__${intermediateAction.actionDefinition.id}${
                isIfThenElseActions ? "_if-then-else" : ""
              }${i}`,
              children
            };
          })
          .filter((item) => item !== null)}
      />
    );
  };

  getDragPreview = (name: string): HTMLDivElement => {
    const crt = document.createElement("div");
    crt.style.position = "absolute";
    crt.style.top = "3px";
    crt.style.left = "15px";
    crt.style.zIndex = "-1";

    const copyIconDiv = document.createElement("div");
    copyIconDiv.className = "kuika-action-tree__drag-preview--copy-icon";
    copyIconDiv.style.display = "flex";
    copyIconDiv.innerHTML = `<svg style="width: 12px; height: 12px;" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="copy-plus" clipPath="url(#clip0_10513_34988)">
        <path id="Vector" d="M7 9.667C7 8.95967 7.28099 8.28131 7.78115 7.78115C8.28131 7.28099 8.95967 7 9.667 7H18.333C18.6832 7 19.03 7.06898 19.3536 7.20301C19.6772 7.33704 19.9712 7.53349 20.2189 7.78115C20.4665 8.0288 20.663 8.32281 20.797 8.64638C20.931 8.96996 21 9.31676 21 9.667V18.333C21 18.6832 20.931 19.03 20.797 19.3536C20.663 19.6772 20.4665 19.9712 20.2189 20.2189C19.9712 20.4665 19.6772 20.663 19.3536 20.797C19.03 20.931 18.6832 21 18.333 21H9.667C9.31676 21 8.96996 20.931 8.64638 20.797C8.32281 20.663 8.0288 20.4665 7.78115 20.2189C7.53349 19.9712 7.33704 19.6772 7.20301 19.3536C7.06898 19.03 7 18.6832 7 18.333V9.667Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path id="Vector_2" d="M4.012 16.737C3.705 16.5626 3.44965 16.31 3.2719 16.0049C3.09415 15.6998 3.00034 15.3531 3 15V5C3 3.9 3.9 3 5 3H15C15.75 3 16.158 3.385 16.5 4" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path id="Vector_3" d="M11 14H17" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path id="Vector_4" d="M14 11V17" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <defs>
        <clipPath id="clip0_10513_34988">
        <rect width="24" height="24" fill="white"/>
        </clipPath>
        </defs>
        </svg>
        `;

    const badgeContainer = document.createElement("div");
    badgeContainer.className = "kuika-action-panel__drag-preview";

    const badge = (
      <KuikaBadge
        className="kuika-padding-right-l"
        content={name}
        leftIcon={<KuikaSvg name="lightning" width={12} height={12} />}
        color="orange"
      />
    );

    ReactDOM.render(badge, badgeContainer);

    crt.appendChild(badgeContainer);
    crt.appendChild(copyIconDiv);

    return crt;
  };

  onDragStartAction = (event: React.DragEvent<HTMLDivElement>, actionDefinition: IActionDefinitionDto) => {
    if (!ActionTreePanel.getIsActionDraggable(actionDefinition)) {
      return;
    }

    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());

    const buffer: HTMLElement | null = document.getElementById("html-buffer");

    const dragPreview: HTMLDivElement = this.getDragPreview(actionDefinition.displayName ?? actionDefinition.name);
    buffer?.appendChild(dragPreview);

    setTimeout(() => {
      _.attempt(() => {
        buffer?.removeChild(dragPreview);
      });
    }, 100);

    event.dataTransfer.setDragImage(dragPreview, dragPreview.clientWidth / 2, dragPreview.clientHeight / 2);

    const payload: IDraggableActionPayload = {
      actionDefinition
    };

    const components = this.props.sd.project.componentSetObj.components;

    if (!components) {
      throw new Error("components should have been truthy at this point");
    }

    const row = components.find((c) => c.name === "Row");

    if (!row) {
      throw new Error("row should have been truthy at this point");
    }

    event.dataTransfer.setData("draggable-action", JSON.stringify(payload));
    event.dataTransfer.setData("id", (Math.floor(Math.random() * 1000000) + 1).toString());
    event.dataTransfer.setData("kuikaid", row.id);
    event.dataTransfer.setData("reason", "create");
    event.dataTransfer.setData(`kuikaComponentType=${row.componentType}`, row.componentType.toString());
  };

  private static getIsActionDraggable = (actionDefinition: IActionDefinitionDto): boolean => {
    return ActionService.getIsActionDefinitionUsableForDataOperations(actionDefinition);
  };

  render() {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    const actions = this.getUniqueActionDefinitionsFromDisplayItems(sd.displayItems);

    const hasSearchResults = actions.some((intermediateAction) => {
      return intermediateAction.diLocations?.some((diL) => {
        if (!this.state.searchText || this.state.searchText === "") return true;

        const searchText = this.state.searchText.toLowerCase();
        return (
          (diL.diRef.id === -1 && this.props.sd.getScreenDesign().name.toLowerCase().includes(searchText)) ||
          diL.diRef.item.name?.toLowerCase().includes(searchText) ||
          diL.diRef.title?.toLowerCase().includes(searchText) ||
          diL.diRef.friendlyName?.toLowerCase().includes(searchText) ||
          diL.event.toLowerCase().includes(searchText) ||
          diL.actionRef.label.toLowerCase().includes(searchText) ||
          diL.actionRef.tag?.toLowerCase().includes(searchText)
        );
      });
    });

    return (
      <div className="kuika-left-panel__container kuika-action-tree">
        <SearchablePanelHeader
          searchText={this.state.searchText}
          onSearchTextChange={this.handleSearchInputChange}
          onSearchClearCallback={this.deleteSearchText}
          viewPart={ViewPart.ActionTreePanel}
          panelName="Action Tree"
          isSearchActive={this.state.isSearchActive}
          setSearchActive={(isSearchActive: boolean) => {
            this.setState({ isSearchActive });
          }}
        />
        <div className="kuika-left-panel__content">
          {actions.length === 0 ? (
            <KuikaEmptyState
              svgName="lightning"
              name="Action"
              description="No actions have been added to this screen yet."
            />
          ) : this.state.searchText && this.state.searchText.length > 0 && !hasSearchResults ? (
            <KuikaEmptyState
              svgName="search-off"
              customName="No Action found"
              description="We couldn't find any items matching your search."
            />
          ) : (
            this.renderActions(actions)
          )}
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ConfirmationMessageChanged ||
      changeReason === ESdChangeReason.StartingScreenIdChanged ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged ||
      changeReason === ESdChangeReason.ChangeDiFriendlyName ||
      changeReason === ESdChangeReason.RecurringIntervalChange
    ) {
      return true;
    }
    return false;
  };
}

const actionTreePanel = withSD<IActionTreePanelProps>(ActionTreePanel);
export { actionTreePanel as ActionTreePanel };
